<script lang="ts" setup>
import type { FetchError } from 'ofetch'
import { useModal } from 'vue-final-modal'

import OldBanner from '~/components/banner/OldBanner.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import SubscriptionInput from '~/components/form-elements/SubscriptionInput.vue'
import CreateDealerApplication from '~/components/modal-windows/CreateDealerApplication.vue'
import ProductsSection from '~/components/sections/ProductsSection.vue'
import constants from '~/constants'
import { bannersForDistributor } from '~/constants/banners'
import Page from '~/enums/page'
import ProductSort from '~/enums/product-sort'
import SubscriptionForm from '~/enums/subscription-form'
import ApiErrorHandler from '~/helpers/api-error-handler'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { ProductData } from '~/types/products'

const { $apiHelper, $toast } = useNuxtApp()

const advantages = [
  {
    imageUrl: '/images/pages/distributor/advantages/1.jpg',
    name: 'Помогаем составить продуктовую матрицу под ваш бизнес для максимизации вашего дохода'
  },
  {
    imageUrl: '/images/pages/distributor/advantages/2.jpg',
    name: 'Выгодные условия и\xA0минимальный порог первого заказа'
  },
  {
    imageUrl: '/images/pages/distributor/advantages/3.jpg',
    name: 'Оперативная логистика'
  },
  {
    imageUrl: '/images/pages/distributor/advantages/4.jpg',
    name: 'Надёжный партнёр: более 3\xA0лет на\xA0рынке, участник госпрограммы по\xA0импортозамещению'
  },
  {
    imageUrl: '/images/pages/distributor/advantages/5.jpg',
    name: 'Широкий выбор качественной продукции'
  }
]
const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const category = constants.categories.ourProducts
const emptyForm = { email: null }
const messages = {
  emailFieldCannotBeEmpty: 'Email не введён.',
  failedToSubscribe: 'Не удалось подписаться.',
  thanksForSubscribing: 'Спасибо за подписку!'
}
const reviews = [
  {
    authorName: 'Анна Макарова',
    avatarUrl: '/images/pages/distributor/reviews/avatar-1.jpg',
    description: 'Косметолог, специалист, работающий в области красоты и\xA0здоровья',
    text: 'Я\xA0являюсь клиентом Дистрибьютора iTAB. Хочу отметить, что iTAB предлагает выгодные условия и\xA0качественный ассортимент, что очень важно сейчас в\xA0наше время. Все быстро оформляется и\xA0отправляется. Приходит все в\xA0хорошем виде. Нравится, что есть минимальный заказ и\xA0доступные цены 👍\n\n'
      + 'Однозначно рекомендую компанию для сотрудничества! В\xA0моей практике они мне очень помогают.',
    score: 5
  }
]
const qualityStandards = [
  {
    description: 'Производства аккредитованы по\xA0международной системе норм и\xA0правил в\xA0отношении лекарственных средств и\xA0пищевых добавок\xA0— GMP (Good Manufacturing Practice).',
    name: 'GMP'
  },
  {
    description: 'На\xA0производствах введена международная система менеджмента в\xA0области безопасности пищевых продуктов.',
    name: 'ISO 22000'
  }
]
const title = 'Оптовым клиентам'
const whoWorksWithUs = [
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/1.jpg',
    name: 'Федеральные торговые сети'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/2.jpg',
    name: 'Аптеки'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/3.jpg',
    name: 'Спортивные клубы'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/4.jpg',
    name: 'Салоны красоты'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/5.jpg',
    name: 'Косметологии'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/6.jpg',
    name: 'Клиники превентивной медицины'
  },
  {
    imageUrl: '/images/pages/distributor/who-works-with-us/7.jpg',
    name: 'Магазины ЗОЖ и\xA0экотоваров'
  }
]

const form = ref({ ...emptyForm })
const isSubscriberCreating = ref(false)

const { data: productsResponse, status: productsStatus } = useLazyAsyncData(
  () => $apiHelper.products.getProducts({
    'filter[categoryId]': category.id,
    'page[size]': 12,
    sort: ProductSort.MostPopular
  }),
  {
    transform: x => JsonApiHelper.denormalizeResponse(x)
  }
)

const productsData = computed<ProductData[]>(() => productsResponse.value?.data ?? [])

useHead({
  title: `${title} — iTAB`,
  meta: [{
    name: 'description',
    content: 'Витамины, добавки и натуральные товары для здоровья оптовым клиентам от крупнейшего дистрибьютора качественных БАД.'
  }]
})

async function openCreateDealerApplicationModalWindow (): Promise<void> {
  await useModal({ component: CreateDealerApplication }).open()
}

async function subscribe (): Promise<void> {
  const { email } = form.value
  const { emailFieldCannotBeEmpty, failedToSubscribe, thanksForSubscribing } = messages

  if (!email) {
    $toast.error(emailFieldCannotBeEmpty)

    return
  }

  isSubscriberCreating.value = true

  try {
    await $apiHelper.subscribers.createSubscriber(email, SubscriptionForm.DistributorPage)

    form.value = { ...emptyForm }

    $toast.success(thanksForSubscribing)
  } catch (error) {
    ApiErrorHandler.handleFetchError(error as FetchError, failedToSubscribe)
  }

  isSubscriberCreating.value = false
}
</script>

<template>
  <div>
    <section>
      <div class="container">
        <Breadcrumbs :current="title" :items="breadcrumbsItems" />

        <h1 class="visually-hidden">
          {{ title }}
        </h1>
      </div>
    </section>

    <section>
      <div class="container">
        <OldBanner :banner="bannersForDistributor[0]" />
      </div>
    </section>

    <section class="section-with-padding mt-10 bg-white">
      <div class="container">
        <h2 class="h1">
          Кто работает с&nbsp;нами?
        </h2>

        <div class="row">
          <div
            v-for="x in whoWorksWithUs"
            :key="x.name"
            class="who-works-with-us-item column-3 min-2-columns tb12 mb-6 box-border flex h-full flex-col"
          >
            <img v-lazy="x.imageUrl" :alt="x.name" class="mb-4" loading="lazy">

            <h3 class="mb-0">
              {{ x.name }}
            </h3>
          </div>
        </div>
      </div>
    </section>

    <section class="application-section section-with-padding">
      <div class="container">
        <div class="application-block">
          <div class="application-block-inner relative flex flex-wrap justify-between">
            <div class="application-block-inner-info">
              <h2 class="text-white">
                Поможем<br>сформировать заказ
              </h2>

              <p class="application-block-inner-info-description text-white">
                Пройдите тест, чтобы сформировать оптимальный ассортимент для заказа
              </p>

              <BasicButton
                color="white"
                is-small
                title="Оставить заявку"
                @click="openCreateDealerApplicationModalWindow"
              />
            </div>
          </div>

          <div class="application-block-image">
            <img v-lazy="'/images/pages/distributor/application/woman.png'" alt="Женщина" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="advantages-section section-with-padding">
      <div class="container">
        <h2 class="h1">
          Преимущества работы<br>с&nbsp;дистрибьютором Айтаб
        </h2>

        <div class="row">
          <div v-for="x in advantages" :key="x.name" class="advantages-section-item column-4 tb12 mb-6">
            <div class="bg-white">
              <img v-lazy="x.imageUrl" :alt="x.name" class="mb-4" loading="lazy">

              <h3 class="mb-0">
                {{ x.name }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Preloader v-if="productsStatus === 'pending'" />
    <ProductsSection
      v-else-if="productsStatus === 'success'"
      class="section-with-padding"
      :path="category.path"
      :products-data="productsData"
      :title="category.name"
    />

    <section class="quality-standards-section section-with-padding bg-white">
      <div class="container">
        <div class="quality-standards-section-container flex flex-wrap items-center justify-between">
          <div class="quality-standards-section-container-info">
            <h2 class="h1">
              Стандарты качества на&nbsp;производствах
            </h2>

            <div v-for="x in qualityStandards" :key="x.name" class="quality-standards-section-container-info-item">
              <h3 class="mb-4">
                {{ x.name }}
              </h3>

              <p>{{ x.description }}</p>
            </div>
          </div>

          <div class="quality-standards-section-container-img">
            <img v-lazy="'/images/pages/distributor/quality-standards/certificate.jpg'" alt="Сертификат" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="price-list-section section-with-padding">
      <div class="container">
        <div class="price-list-section-block">
          <div class="price-list-section-block-inner flex flex-wrap justify-between">
            <div class="price-list-section-block-inner-info">
              <h2 class="text-white">
                Дарим 5000&nbsp;₽ на&nbsp;образцы продуктов уже при первом заказе
              </h2>

              <BasicButton
                color="white"
                is-small
                title="Получить прайс-лист"
                @click="openCreateDealerApplicationModalWindow"
              />
            </div>
          </div>

          <div class="price-list-section-block-image">
            <img v-lazy="'/images/pages/distributor/price-list/headline.png'" alt="Headline" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="reviews-section section-with-padding">
      <div class="container">
        <div class="reviews-slider">
          <div class="h2">
            Отзывы
          </div>

          <div v-for="x in reviews" :key="x.authorName" class="reviews-slider-slide bg-white">
            <div class="reviews-slider-author">
              <div class="flex">
                <div class="reviews-slider-author-avatar">
                  <img v-lazy="x.avatarUrl" :alt="x.authorName" loading="lazy">
                </div>

                <div class="reviews-slider-author-info">
                  <div class="reviews-slider-author-rating mb-1">
                    <Score :score="x.score" />
                  </div>

                  <div class="reviews-slider-author-info-name mb-1">
                    {{ x.authorName }}
                  </div>

                  <p class="reviews-slider-author-info-info flex items-center">
                    {{ x.description }}
                  </p>
                </div>
              </div>

              <p class="reviews-slider-author-text pre-wrap">
                {{ x.text }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="subscription-section section-with-padding">
      <div class="container">
        <div class="subscription-block">
          <div class="subscription-block-inner flex flex-wrap justify-between">
            <div class="subscription-block-info">
              <h2 class="subscription-block-title text-white">
                Подпишитесь на&nbsp;нашу рассылку, чтобы оперативно получать специальные предложения по&nbsp;оптовым закупкам
              </h2>
            </div>

            <div class="subscription">
              <SubscriptionInput
                v-model="form.email"
                class="mb-2"
                :is-subscribing="isSubscriberCreating"
                placeholder="Почта"
                @on-button-click="subscribe"
              />

              <Agreement icon-name="outlined/east" is-white />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/css/variables';

.section-with-padding {
  box-sizing: border-box;
  padding: 40px 0;

  @media screen and (max-width: 767px) {
    padding: 32px 0;
  }
}

.who-works-with-us-item {
  h3 {
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.25em;

    @media (max-width: 580px) {
      font-size: 16px;
    }
  }

  img {
    border-radius: 12px;
    height: 218px;
    max-width: 100%;
    min-width: 100%;
    object-fit: cover;
    width: 100%;

    @media (max-width: 580px) {
      height: 120px;
    }
  }
}

.application-section {
  background-color: #08c379;
  background-image: url(/images/pages/distributor/application/background.png);
  background-repeat: no-repeat;
  background-size: 100% 100%;

  .application-block {
    position: relative;

    &-image {
      position: relative;

      img {
        bottom: 0;
        max-width: 100%;
        position: absolute;
        right: 0;
        transform: translateY(40px);

        @media screen and (max-width: 767px) {
          position: static;
          transform: translateY(36px);
        }
      }
    }

    &-inner {
      &-info {
        width: 30%;

        @media screen and (max-width: 768px) {
          width: 100%;
        }

        &-description {
          margin-bottom: 30px;
        }

        h2 {
          margin-bottom: 18px;
        }
      }
    }
  }
}

.advantages-section {
  &-item > div {
    border-radius: 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;

    h3 {
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: 1.25em;

      @media (max-width: 580px) {
        font-size: 16px;
      }
    }

    img {
      border-radius: 12px;
      height: 218px;
      max-width: 100%;
      min-width: 100%;
      object-fit: cover;
      width: 100%;
    }
  }
}

.quality-standards-section-container {
  @media (max-width: 768px) {
    flex-direction: column;
  }

  &-img {
    width: 60%;

    @media (max-width: 768px) {
      margin-top: 32px;
      width: 100%;
    }

    img {
      border-radius: 12px;
      max-width: 100%;
    }
  }

  &-info {
    width: 35%;

    @media (max-width: 768px) {
      margin: 0;
      width: 100%;
    }

    &-item {
      margin-bottom: 32px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.price-list-section {
  background: #8080ff;

  &-block {
    &-inner {
      &-info {
        width: 50%;

        @media (max-width: 768px) {
          width: 100%;
        }
      }
    }

    &-image {
      position: relative;

      img {
        bottom: 0;
        max-width: 100%;
        position: absolute;
        right: 0;
        transform: translateY(40px);

        @media (max-width: 385px) {
          position: static;
          transform: translateY(36px);
        }

        @media (max-width: 768px) {
          position: static;
        }
      }
    }
  }
}

.reviews-section {
  .reviews-slider-slide {
    border-radius: 12px;
    padding: 24px;
  }

  .reviews-slider-author {
    &-avatar {
      margin-right: 16px;

      img {
        border-radius: 50%;
        height: 100px;
        width: 100px;

        @media (max-width: 385px) {
          height: 64px;
          width: 64px;
        }
      }
    }

    &-rating {
      font-size: 24px;
    }

    &-info {
      &-info {
        color: #ababab;
        font-size: 14px;
        line-height: 14px;

        @media (max-width: 385px) {
          justify-content: center;
        }
      }

      &-name {
        color: #404e5e;
        font-size: 22px;
        font-style: normal;
        font-weight: 500;
        line-height: 1.5em;

        @media (max-width: 580px) {
          font-size: 16px;
        }
      }
    }

    &-inner {
      margin-bottom: 24px;

      @media (max-width: 580px) {
        margin-bottom: 16px;
      }
    }

    &-text {
      text-align: start;

      @media (max-width: 580px) {
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
      }
    }
  }
}

.subscription-section {
  background: variables.$main-green-color;

  .subscription-block-inner {
    @media (max-width: 768px) {
      display: block;
    }
  }

  .subscription-block-info {
    width: 52%;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  h2 {
    margin-bottom: 0;

    @media (max-width: 580px) {
      font-size: 20px;
      font-weight: 500;
      line-height: 23px;
    }

    @media (max-width: 768px) {
      margin-bottom: 20px;
    }
  }

  .subscription {
    width: 42%;

    @media (max-width: 768px) {
      width: 100%;
    }
  }
}
</style>
